import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { queryFor, useApiConfig } from "@visioni/a3s-client-api/app";
import {
  getTenantActivationEndpoint,
  getFleetActivationEndpoint,
  listTenantActivationsEndpoint,
  listFleetActivationsEndpoint,
  type ActivationDetail,
  type ActivationSummary,
  type FleetActivationDetail,
  type FleetActivationSummary,
} from "../../client-messages/activations";
import { listTenantSignalsEndpoint, type SignalSummary } from "../../client-messages/signals";
import { activationFiltersKey, type TenantActivationFilters, type FleetActivationFilters } from "./activation-filters";
import type { TenantSignalFilters } from "./signal-filters";
import { useCursorPager, type CursorPager } from "./cursor-pager";

const listActivationsQuery = queryFor(listTenantActivationsEndpoint);
const getActivationQuery = queryFor(getTenantActivationEndpoint);
const listFleetActivationsQuery = queryFor(listFleetActivationsEndpoint);
const getFleetActivationQuery = queryFor(getFleetActivationEndpoint);
const listSignalsQuery = queryFor(listTenantSignalsEndpoint);

/** Rows per page of an activation list. The endpoint caps at 100. */
export const ACTIVATIONS_PAGE_SIZE = 25;

export interface UseTenantActivationsOptions {
  readonly filters: TenantActivationFilters;
  /** Gate the query — e.g. while the filters in the URL are invalid. */
  readonly enabled?: boolean;
}

export interface UseActivationsResult<T = ActivationSummary> {
  readonly items: readonly T[] | undefined;
  readonly isLoading: boolean;
  /** True while any read is in flight, including a page change still showing the previous page. */
  readonly isFetching: boolean;
  /** True while the rows on screen are the previous page's, held while the next one loads. */
  readonly isPlaceholderData: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
  readonly pager: CursorPager;
  /** The cursor the page on screen returned; `null` on the last page. */
  readonly nextCursor: string | null;
}

/**
 * One page of a tenant's Patriot activations, newest first.
 *
 * Paged rather than accumulated, so the list holds its place while support
 * opens a ticket and comes back. Changing the filters returns to page one.
 */
export function useTenantActivations({ filters, enabled = true }: UseTenantActivationsOptions): UseActivationsResult {
  const apiConfig = useApiConfig();
  const pager = useCursorPager(activationFiltersKey(filters));

  const query = useQuery({
    ...listActivationsQuery(
      apiConfig,
      {},
      {
        limit: ACTIVATIONS_PAGE_SIZE,
        ...filters,
        ...(pager.cursor !== undefined && { cursor: pager.cursor }),
      },
    ),
    enabled,
    placeholderData: keepPreviousData,
  });

  return {
    items: query.data?.items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: () => void query.refetch(),
    pager,
    // While a placeholder is showing, its cursor belongs to the page being
    // left, not the one being loaded — following it would skip a page.
    nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
  };
}

export interface UseFleetActivationsOptions {
  readonly filters: FleetActivationFilters;
  /** Gate the query — e.g. while the filters in the URL are invalid. */
  readonly enabled?: boolean;
}

/**
 * One page of every activation in the replica, newest first — unattributed
 * ones included, each row naming its own tenant.
 *
 * Paged like {@link useTenantActivations}, with one difference a caller must handle:
 * a page can be empty with more behind it, when the months it walked held
 * nothing. `nextCursor` is what says whether there is more, never the row count.
 */
export function useFleetActivations({
  filters,
  enabled = true,
}: UseFleetActivationsOptions): UseActivationsResult<FleetActivationSummary> {
  const apiConfig = useApiConfig();
  const pager = useCursorPager(activationFiltersKey(filters));

  const query = useQuery({
    ...listFleetActivationsQuery(
      apiConfig,
      {},
      {
        limit: ACTIVATIONS_PAGE_SIZE,
        ...filters,
        ...(pager.cursor !== undefined && { cursor: pager.cursor }),
      },
    ),
    enabled,
    placeholderData: keepPreviousData,
  });

  return {
    items: query.data?.items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: () => void query.refetch(),
    pager,
    nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
  };
}

/** Rows per page of a signal list. The endpoint caps at 100. */
export const SIGNALS_PAGE_SIZE = 25;

export interface UseTenantSignalsOptions {
  readonly filters: TenantSignalFilters;
  /** Gate the query — e.g. while the filters in the URL are invalid. */
  readonly enabled?: boolean;
}

/** A page of signals, held and paged exactly as a page of activations is. */
export type UseSignalsResult = UseActivationsResult<SignalSummary>;

/**
 * One page of a tenant's Patriot signals, newest first, including those that
 * raised no activation.
 *
 * Paged like {@link useTenantActivations}: changing the filters returns to page one.
 */
export function useTenantSignals({ filters, enabled = true }: UseTenantSignalsOptions): UseSignalsResult {
  const apiConfig = useApiConfig();
  const pager = useCursorPager(activationFiltersKey(filters));

  const query = useQuery({
    ...listSignalsQuery(
      apiConfig,
      {},
      {
        limit: SIGNALS_PAGE_SIZE,
        ...filters,
        ...(pager.cursor !== undefined && { cursor: pager.cursor }),
      },
    ),
    enabled,
    placeholderData: keepPreviousData,
  });

  return {
    items: query.data?.items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: () => void query.refetch(),
    pager,
    nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
  };
}

export interface UseActivationResult<T = ActivationDetail> {
  readonly activation: T | undefined;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

/** One activation by ticket from the tenant route, which serves it only to the tenant it belongs to. */
export function useTenantActivation(ticketNo: string): UseActivationResult {
  const apiConfig = useApiConfig();

  const query = useQuery({
    ...getActivationQuery(apiConfig, { ticketNo }),
    enabled: ticketNo !== "",
  });

  return {
    activation: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

/** One activation by ticket from the fleet route, whichever tenant it belongs to — or none. */
export function useFleetActivation(ticketNo: string): UseActivationResult<FleetActivationDetail> {
  const apiConfig = useApiConfig();

  const query = useQuery({
    ...getFleetActivationQuery(apiConfig, { ticketNo }),
    enabled: ticketNo !== "",
  });

  return {
    activation: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}
